import React, { useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AppFooter from '@core/components/AppFooter';
import { ErrorResponse } from '@core/types/errorResponse';
import { forgotPasswordAsync } from '@features/auth/services/forgotPasswordSvc';
import { ForgotPasswordRequest } from '@features/auth/types/forgotPassword';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import loginImage from '../login/login.png';
import '../login/login.css';
import InfoTooltip from '@core/components/InfoTooltip';

type FieldErrors = Partial<Record<keyof ForgotPasswordRequest, string[]>>;

const ForgotPassword: React.FC = () => {
  const [form, setForm] = useState<ForgotPasswordRequest>({ email: '' });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const showError = useApiError();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Clear error when field changes
    if (formErrors[name as keyof ForgotPasswordRequest]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name as keyof ForgotPasswordRequest];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const response = await forgotPasswordAsync(form);

      if (response.isSuccess()) {
        setFormErrors({});
        showSuccess("Please check your mail for the token");
        navigate("/reset-password");
      } else {
        const error = response.getError() as ErrorResponse;
        setFormErrors((error?.errors as FieldErrors) ?? {});
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper min-vh-100 d-flex flex-column">
      <main
        id="main"
        className="flex-grow-1 d-flex align-items-center justify-content-center py-4"
        tabIndex={-1}
        data-testid="forgot-password-page"
      >
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="login-card shadow-lg border-0" data-testid="forgot-password-card">
                <Row className="g-0">
                  <Col
                    md={6}
                    className="d-none d-md-flex align-items-center justify-content-center login-image bg-light"
                    data-testid="forgot-password-illustration-panel"
                  >
                    <img src={loginImage} alt="NTCOG Logo" className="img-fluid" />
                  </Col>
                  <Col md={6} className="d-flex align-items-center">
                    <Card.Body className="p-4 p-md-5 w-100 auth-card__body">
                      <h1 className="mb-4 text-center fw-bold text-dark auth-page-heading">Forgot Password</h1>
                      <p className="text-center text-muted mb-4">
                        Enter your account email and we&apos;ll send you a password reset token.
                      </p>

                      <Form
                        noValidate
                        onSubmit={handleSubmit}
                        className="login-form"
                        aria-label="Forgot password form"
                        data-testid="forgot-password-form"
                      >
                        <Form.Group className="mb-4" controlId="email">
                          <Form.Label>Email address<InfoTooltip text="Enter the email address for your account. We will send a reset link there." /></Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            placeholder="name@example.com"
                            value={form.email}
                            onChange={handleChange}
                            autoComplete="email"
                            isInvalid={!!formErrors.email}
                            data-testid="forgot-password-email-input"
                          />
                          <Form.Control.Feedback type="invalid" role="alert">
                            {formErrors.email?.[0]}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          className="w-100 mb-3"
                          disabled={loading}
                          aria-busy={loading}
                          data-testid="forgot-password-submit-button"
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Sending…
                            </>
                          ) : (
                            'Continue'
                          )}
                        </Button>

                        <div className="auth-secondary-link-wrap">
                          <Button
                            variant="link"
                            className="text-muted p-0 forgot-password auth-secondary-link"
                            onClick={() => navigate('/login')}
                            data-testid="forgot-password-login-link"
                          >
                            Login to your account
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>
      <AppFooter />
    </div>
  );
};

export default ForgotPassword;
